import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, Navigate } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { updateStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/update';
import { createWorkOrderStockReceipt } from '#/api/momMgmt/workOrder/inbound';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { useFormValidationSummary } from '#/hooks/useFormValidationSummary';
import {
  extractUnitOptions,
  useMaterialUnitOptions,
} from '#/modules/imsMgmt/helper/materialUnit';
import { canEditStockReceipt } from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import { WORK_ORDER_STATUS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { toast } from '#/utils/toast';
import {
  formatDisplayAmount,
  formatDisplayQty,
} from '#/utils/formatNumber';
import {
  lookupQueryKey,
  stockReceiptDetailQueryKey,
  workOrderDetailQueryKey,
} from '#/utils/queryKeys';

import WorkOrderInboundPackagesEditor from './WorkOrderInboundPackagesEditor';
import {
  mapInboundFormToCreatePayload,
  mapStockReceiptDetailToInboundForm,
} from '../helper/mapFormToCreatePayload';
import {
  WORK_ORDER_INBOUND_FIELD_LABELS,
  buildInboundDefaultValues,
  workOrderInboundFormSchema,
} from '../schemas/inboundSchema';

const WorkOrderInboundOperateForm = ({ detail, receiptId = null }) => {
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const workOrderId = detail?.id;
  const isEdit = Boolean(receiptId);
  const canOperate = detail?.status === WORK_ORDER_STATUS.PROCESSING;
  const mtrId = detail?.mtr?.id ?? detail?.mtrId ?? '';

  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const defaultValues = useMemo(
    () => buildInboundDefaultValues(detail),
    [detail],
  );

  const initialUnitOptions = useMemo(
    () => extractUnitOptions(detail?.mtr),
    [detail?.mtr],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(workOrderInboundFormSchema),
    defaultValues,
  });

  const packages = useWatch({ control, name: 'packages', defaultValue: [] });
  const unitPrice = useWatch({ control, name: 'unitPrice' });
  const warehouseId = useWatch({ control, name: 'warehouseId' }) || '';
  const unitId = useWatch({ control, name: 'unitId', defaultValue: '' });

  const {
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const warehouseQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const receiptQuery = useQuery({
    queryKey: stockReceiptDetailQueryKey(receiptId),
    queryFn: () => fetchStockReceiptDetail(receiptId),
    enabled: isEdit,
  });
  const receipt = receiptQuery.data?.data ?? null;

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }
    if (isEdit && !receipt) {
      return;
    }
    if (String(unitId ?? '').trim()) {
      return;
    }
    setValue('unitId', defaultUnitId, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    unitId,
    setValue,
    isEdit,
    receipt,
  ]);

  useEffect(() => {
    if (!isEdit || !receipt) {
      return;
    }
    updatedAtRef.current = receipt.updatedAt
      ?? receipt.record?.updatedAt
      ?? null;
    reset(mapStockReceiptDetailToInboundForm(receipt));
  }, [isEdit, receipt, reset]);

  const warehouseOptions = useMemo(
    () => warehouseQuery.data ?? [],
    [warehouseQuery.data],
  );

  const totals = useMemo(() => {
    const totalQty = (packages ?? []).reduce(
      (sum, row) => sum + (Number(row?.qty) || 0),
      0,
    );
    const price = Number(unitPrice) || 0;
    const totalPrice = Math.round(totalQty * price * 10000) / 10000;
    return { totalQty, totalPrice };
  }, [packages, unitPrice]);

  const mtrName = detail?.mtr?.name || '—';
  const mtrNo = detail?.mtr?.mtrNo || '';
  const formDisabled = !canOperate;

  const saveMutation = useMutation({
    mutationFn: (values) => {
      const payload = mapInboundFormToCreatePayload(values, detail);
      if (!isEdit) {
        return createWorkOrderStockReceipt(workOrderId, payload);
      }
      return updateStockReceipt(receiptId, {
        ...payload,
        updatedAt: updatedAtRef.current,
      });
    },
    onSuccess: async (result) => {
      const savedMessage = isEdit ? '入庫單已更新' : '入庫單已建立';
      toast.success(result?.message || savedMessage);
      resetValidationSummary();
      if (isEdit) {
        const next = result?.data;
        updatedAtRef.current = next?.updatedAt
          ?? next?.record?.updatedAt
          ?? updatedAtRef.current;
        await queryClient.invalidateQueries({
          queryKey: stockReceiptDetailQueryKey(receiptId),
        });
      } else {
        reset(buildInboundDefaultValues(detail));
      }
      await queryClient.invalidateQueries({
        queryKey: workOrderDetailQueryKey(workOrderId),
      });
    },
    onError: (error) => {
      if (isEdit) {
        toast.error(getStockReceiptApiErrorMessage(error, '更新入庫單失敗'));
        if (isStockReceiptVersionConflict(error)) {
          receiptQuery.refetch();
        }
        return;
      }
      toast.error(getWorkOrderApiErrorMessage(error, '入庫失敗'));
    },
  });

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!canOperate) {
      toast.error('僅生產中工單可入庫');
      return;
    }
    if (isEdit && !canEditStockReceipt(receipt?.status)) {
      toast.error('僅草稿入庫單可編輯');
      return;
    }
    resetValidationSummary();
    saveMutation.mutate(values);
  }

  if (isEdit && receiptQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        {getStockReceiptApiErrorMessage(receiptQuery.error, '載入入庫單失敗')}
      </p>
    );
  }

  if (isEdit && receipt && !canEditStockReceipt(receipt.status)) {
    return (
      <Navigate to={ROUTES.workOrderInbound(workOrderId)} replace />
    );
  }

  if (isEdit && receiptQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">載入入庫單…</p>;
  }

  return (
    <form
      className="sheet"
      onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
      noValidate
    >
      {!canOperate ? (
        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          僅
          <span className="font-medium">生產中（PROCESSING）</span>
          工單可操作入庫。
        </p>
      ) : null}

      <FormValidationSummary
        show={isSubmitAttempted}
        errors={errors}
        fieldLabels={WORK_ORDER_INBOUND_FIELD_LABELS}
        className="mb-4"
      />

      <div className="create_edit_form__body divide-y divide-gray-500">
        <section className="pb-6">
          <div className="form-block">
            <h2 className="form-block__title">單據資訊</h2>
          </div>
          <div className="mb-3 grid grid-cols-1 gap-3 @4xl/sheet:grid-cols-2">
            <FloatingDateInput
              id="receiptDate"
              label="單據日"
              control={control}
              errors={errors}
              disabled={formDisabled || saveMutation.isPending}
            />
            <FloatingSelect
              id="warehouseId"
              label="入庫倉"
              options={warehouseOptions}
              control={control}
              errors={errors}
              isDisabled={
                formDisabled
                || saveMutation.isPending
                || warehouseQuery.isLoading
              }
              noOptionsMessage="尚無可用倉別"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 @4xl/sheet:grid-cols-2">
            <FloatingTextarea
              id="issueReason"
              label="入庫事由"
              register={register}
              errors={errors}
              rows={2}
              disabled={formDisabled || saveMutation.isPending}
            />
            <FloatingTextarea
              id="remark"
              label="備註"
              register={register}
              errors={errors}
              rows={2}
              disabled={formDisabled || saveMutation.isPending}
            />
          </div>
        </section>

        <section className="pt-4 pb-6">
          <div className="form-block">
            <h2 className="form-block__title">物料</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="px-3 py-2 rounded-md border border-slate-200 bg-slate-50">
              <p className="text-xs text-muted-foreground">入庫物料（唯讀）</p>
              <p className="truncate text-sm font-medium text-slate-800">{mtrName}</p>
              {mtrNo ? (
                <p className="truncate text-xs text-muted-foreground">{mtrNo}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-end gap-4 text-sm text-muted-foreground">
              <p>
                總數量
                <span className="ml-1 font-semibold text-primary-700">
                  {formatDisplayQty(totals.totalQty)}
                </span>
              </p>
              <p>
                總價
                <span className="ml-1 font-semibold text-primary-700">
                  {formatDisplayAmount(totals.totalPrice)}
                </span>
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FloatingSelect
                id="unitId"
                label="單位"
                options={unitSelectOptions}
                control={control}
                errors={errors}
                isDisabled={
                  formDisabled
                  || saveMutation.isPending
                  || !mtrId
                  || unitOptionsLoading
                }
                noOptionsMessage={
                  !mtrId ? '尚無成品物料' : '此物料尚無可選單位'
                }
              />
              <FloatingInput
                id="unitPrice"
                label="單價"
                type="number"
                register={register}
                errors={errors}
                disabled={formDisabled || saveMutation.isPending}
              />
            </div>
            <FloatingTextarea
              id="materialRemark"
              label="物料備註"
              register={register}
              errors={errors}
              disabled={formDisabled || saveMutation.isPending}
            />
          </div>

          <WorkOrderInboundPackagesEditor
            control={control}
            register={register}
            errors={errors}
            warehouseId={warehouseId}
            disabled={formDisabled || saveMutation.isPending}
          />
        </section>
      </div>

      <div className="create_edit_form__actions stock-ops-actions mt-4 flex flex-wrap justify-end gap-2">
        {isEdit ? (
          <Button type="button" variant="outline" asChild>
            <Link to={ROUTES.workOrderInbound(workOrderId)}>取消</Link>
          </Button>
        ) : null}
        <Button
          type="submit"
          disabled={formDisabled || saveMutation.isPending}
        >
          {saveMutation.isPending
            ? (isEdit ? '儲存中…' : '送出中…')
            : (isEdit ? '儲存' : '送出')}
        </Button>
      </div>
    </form>
  );
};

WorkOrderInboundOperateForm.propTypes = {
  detail: PropTypes.object.isRequired,
  receiptId: PropTypes.string,
};

export default WorkOrderInboundOperateForm;
